import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { forwardRef, memo, useMemo, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { useCommentsRoot } from 'components/Comments/Root';

import { sendEvent } from 'lib/gtag';
import { noop } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import CommentListItemSkeleton from '../Skeleton';
import Body from './Body';
import Footer from './Footer';
import Header from './Header';

import COMMENT from 'constants/comment';

const Editing = dynamic(() => import('../Editing'), { ssr: false });

const CommentItem = forwardRef(
  (
    {
      canBeRecommendedAsReview,
      children,
      commentDate,
      commentFixed = false,
      commentId,
      commentLikes,
      commentStatusName,
      commentText,
      hasReply = false,
      isDisliked = false,
      isEdited,
      isLiked = false,
      isReply = false,
      isReview = false,
      isShowingReplies = false,
      parentId,
      userId,
      userLevel,
      userName,
      userPhoto,
      userSpecialtyName = null,
      userTypeName,
      userUsername,
      onToggleReplies = noop,
    },
    ref
  ) => {
    const [isEditing, setEditing] = useState(false);
    const {
      activeAnswer,
      gaEventCategory,
      addDislike,
      addLike,
      setActiveAnswer,
    } = useCommentsRoot();
    const {
      loadingLoggedInUser,
      LoggedInUser,
      isLoggedIn,
      runCallbackIfLoggedIn,
    } = useUser();

    /**
     * This is a control made for content blocked comment statuses (USELESS,
     * PARTIALLY_REMOVED) that can be seen if the user wants to do so.
     */
    const [
      isContentBlockedViewableCommentHidden,
      setContentBlockedViewableCommentHidden,
    ] = useState(true);

    const isContentBlocked = useMemo(
      () =>
        commentStatusName === COMMENT.STATUSES.REMOVED_BY_ADMIN ||
        commentStatusName === COMMENT.STATUSES.REMOVED_BY_USER ||
        (LoggedInUser?.userId !== userId &&
          commentStatusName === COMMENT.STATUSES.REMOVED_FOR_ALL) ||
        (commentStatusName === COMMENT.STATUSES.USELESS &&
          isContentBlockedViewableCommentHidden &&
          LoggedInUser?.userId !== userId) ||
        (commentStatusName === COMMENT.STATUSES.PARTIALLY_REMOVED &&
          isContentBlockedViewableCommentHidden &&
          LoggedInUser?.userId !== userId),
      [
        commentStatusName,
        loadingLoggedInUser,
        isContentBlockedViewableCommentHidden,
      ]
    );

    const onEditCommentClick = () => setEditing(true);
    const onStopEditing = () => setEditing(false);

    /*
      Handlers do Footer vivem aqui (antes estavam no Body) porque o
      Footer saiu de dentro do card cinza e passou a ser irmão do Body
      na árvore do Item — reduz props drilling desnecessário.
    */
    const onLikeClickHandler = () => {
      if (!isLoggedIn) {
        sendEvent({
          action: 'like_comment_attempt_unlogged',
          category: gaEventCategory,
        });
      }
      runCallbackIfLoggedIn(() => {
        sendEvent({
          action: isLiked ? 'remove_like_comment' : 'add_like_comment',
          category: gaEventCategory,
        });
        addLike(commentId);
      });
    };

    const onDislikeClickHandler = () => {
      if (!isLoggedIn) {
        sendEvent({
          action: 'dislike_comment_unlogged',
          category: gaEventCategory,
        });
      }
      runCallbackIfLoggedIn(() => {
        sendEvent({
          action: isDisliked ? 'remove_dislike_comment' : 'add_dislike_comment',
          category: gaEventCategory,
        });
        addDislike(commentId);
      });
    };

    /*
      Ao clicar em "Responder", em vez de abrir um textbox inline, a
      gente só sinaliza no `Comments.Root` qual comentário está sendo
      respondido. O textbox único no rodapé da aba lê esse contexto e
      passa a mostrar "Respondendo a @usuario" — estilo Pelando.
    */
    const onReplyHandler = () => {
      if (!isLoggedIn) {
        sendEvent({ action: 'reply_unlogged', category: gaEventCategory });
      }

      /*
        Replica o comportamento original do Body.js: o evento `reply`
        só dispara quando o usuário troca de alvo (clica em Responder
        num comentário diferente do já ativo). Evita duplicidade se
        clicar duas vezes no mesmo botão Responder.
      */
      if (activeAnswer?.commentId !== commentId) {
        sendEvent({ action: 'reply', category: gaEventCategory });
      }

      setActiveAnswer({
        commentId,
        parentId: parentId || commentId,
        userUsername,
      });
    };

    if (loadingLoggedInUser) {
      return <CommentListItemSkeleton />;
    }

    const showFooter = !isEditing && !isContentBlocked && !commentFixed;

    return (
      <div className={twJoin('flex w-full flex-col', isReply && 'pl-10')}>
        {/*
          Card cinza com Header + texto. O Footer (Reagir/Responder) é
          renderizado logo abaixo FORA deste bloco, sem bg — assim as
          reações "flutuam" sobre o fundo da aba como no Pelando.
        */}
        <div
          ref={ref}
          className="rounded-4 bg-neutral-high-200 px-4 py-2 dark:bg-neutral-low-400"
        >
          <Header
            canBeRecommendedAsReview={canBeRecommendedAsReview}
            commentDate={commentDate}
            commentFixed={commentFixed}
            commentId={commentId}
            gaEventCategory={gaEventCategory}
            isContentBlocked={isContentBlocked}
            isEdited={isEdited}
            isReply={isReply}
            isReview={isReview}
            userId={userId}
            userLevel={userLevel}
            userName={userName}
            userPhoto={userPhoto}
            userSpecialtyName={userSpecialtyName}
            userTypeName={userTypeName}
            userUsername={userUsername}
            onEditCommentClick={onEditCommentClick}
          />
          {isEditing ? (
            <Editing
              commentId={commentId}
              commentText={commentText}
              gaEventCategory={gaEventCategory}
              parentId={parentId}
              onStopEditing={onStopEditing}
            />
          ) : (
            <Body
              commentFixed={commentFixed}
              commentStatusName={commentStatusName}
              commentText={commentText}
              isContentBlocked={isContentBlocked}
              onShowCommentLinkClick={() => {
                setContentBlockedViewableCommentHidden(false);
              }}
            />
          )}
        </div>
        {showFooter && (
          <Footer
            commentId={commentId}
            commentLikes={commentLikes}
            hasReply={hasReply}
            isDisliked={isDisliked}
            isLiked={isLiked}
            isReply={isReply}
            isShowingReplies={isShowingReplies}
            onDislike={onDislikeClickHandler}
            onLike={onLikeClickHandler}
            onReply={onReplyHandler}
            onToggleReplies={onToggleReplies}
          />
        )}
        {children}
      </div>
    );
  }
);

CommentItem.displayName = 'CommentItem';

CommentItem.propTypes = {
  canBeRecommendedAsReview: PropTypes.bool,
  children: PropTypes.node,
  commentDate: PropTypes.string.isRequired,
  commentFixed: PropTypes.bool,
  commentId: PropTypes.number.isRequired,
  commentLikes: PropTypes.number,
  commentStatusName: PropTypes.string.isRequired,
  /**
   * Should be required but there are edge cases where it could be null.
   * There were bugs where users could send an empty comment
   */
  commentText: PropTypes.string,
  hasReply: PropTypes.bool,
  isDisliked: PropTypes.bool,
  isEdited: PropTypes.bool.isRequired,
  isLiked: PropTypes.bool,
  isReply: PropTypes.bool,
  isReview: PropTypes.bool,
  isShowingReplies: PropTypes.bool,
  parentId: PropTypes.number,
  userId: PropTypes.number.isRequired,
  userLevel: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userTypeName: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
  onToggleReplies: PropTypes.func,
};

export default memo(CommentItem);
